import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { StudyFormInputs } from './StudyFormInputs';
import {
    StudyFormDefinition,
    StudyFormImageInfo,
    StudyFormQuestion,
    StudyFormQuestionType,
} from '@wwf/sdk';
import { StudyFormInput } from '@domain/studyForm';

type DrawerMode = 'create' | 'edit' | 'show' | 'version';

const defaultValues: StudyFormInput = {
    id: '',
    title: '',
    enabled: true,
    missionTypeId: '' as any,
    whoCanFill: 'VOLUNTEER',
    formDefinition: {
        withMascotHug: false,
        questions: [],
    },
};

function isQuestionType(value: unknown): value is StudyFormQuestionType {
    return (
        value === 'YES_NO' ||
        value === 'TWO_CHOICES' ||
        value === 'IMAGE_GALLERY'
    );
}

function normalizeImageInfo(value: unknown): StudyFormImageInfo | null {
    if (!value || typeof value !== 'object') return null;
    const image = value as Partial<
        StudyFormImageInfo & {
            locale?: string;
            name?: string;
            label?: string;
        }
    >;

    if (typeof image.key === 'string' && typeof image.image === 'string') {
        return {
            key: image.key,
            name:
                typeof image.name === 'string'
                    ? image.name
                    : typeof image.label === 'string'
                      ? image.label
                      : image.key,
            image: image.image,
        };
    }

    // Legacy fallback from locale/name based snapshots.
    if (typeof image.locale === 'string') {
        return {
            key: image.locale,
            name: image.locale,
            image: '',
        };
    }

    if (typeof image.name !== 'string') {
        return null;
    }
    return {
        key: image.name,
        name: image.name,
        image: '',
    };
}

function normalizeQuestion(value: unknown): StudyFormQuestion | null {
    if (!value || typeof value !== 'object') return null;
    const question = value as Partial<StudyFormQuestion>;
    if (
        typeof question.id !== 'string' ||
        typeof question.title !== 'string' ||
        !isQuestionType(question.type)
    ) {
        return null;
    }

    if (question.type === 'YES_NO') {
        return {
            id: question.id,
            title: question.title,
            type: 'YES_NO',
            subQuestion:
                typeof question.subQuestion === 'string'
                    ? question.subQuestion
                    : null,
        };
    }

    if (question.type === 'TWO_CHOICES') {
        return {
            id: question.id,
            title: question.title,
            type: 'TWO_CHOICES',
            choice1:
                typeof question.choice1 === 'string' ? question.choice1 : '',
            choice2:
                typeof question.choice2 === 'string' ? question.choice2 : '',
        };
    }

    const imageGallery = question as Partial<{ images: unknown[] }>;
    return {
        id: question.id,
        title: question.title,
        type: 'IMAGE_GALLERY',
        images: Array.isArray(imageGallery.images)
            ? imageGallery.images
                  .map(normalizeImageInfo)
                  .filter(
                      (
                          item: StudyFormImageInfo | null
                      ): item is StudyFormImageInfo => item !== null
                  )
            : [],
    };
}

function normalizeFormDefinition(
    value: StudyFormDefinition | null | undefined
): StudyFormDefinition | null | undefined {
    if (!value) {
        return defaultValues.formDefinition;
    }

    return {
        withMascotHug: value.withMascotHug,
        questions: Array.isArray(value.questions)
            ? value.questions
                  .map(normalizeQuestion)
                  .filter((item): item is StudyFormQuestion => item !== null)
            : [],
    };
}

function sanitizeQuestion(question: StudyFormQuestion): StudyFormQuestion {
    if (question.type === 'YES_NO') {
        return {
            ...question,
            id: question.id.trim(),
            title: question.title.trim(),
            subQuestion: question.subQuestion?.trim() || null,
        };
    }
    if (question.type === 'TWO_CHOICES') {
        return {
            ...question,
            id: question.id.trim(),
            title: question.title.trim(),
            choice1: question.choice1.trim(),
            choice2: question.choice2.trim(),
        };
    }
    return {
        ...question,
        id: question.id.trim(),
        title: question.title.trim(),
        images: (question.images ?? []).map(image => ({
            key: image.key.trim(),
            name: (image.name ?? image.key).trim(),
            image: image.image.trim(),
        })),
    };
}

function sanitizeFormDefinition(
    formDefinition: StudyFormDefinition
): StudyFormDefinition {
    return {
        withMascotHug: formDefinition.withMascotHug,
        questions: formDefinition.questions.map(sanitizeQuestion),
    };
}

function validateFormDefinition(
    formDefinition: StudyFormDefinition | null | undefined
) {
    if (!formDefinition || !Array.isArray(formDefinition.questions)) {
        return 'Form must contain a questions array';
    }
    for (const question of formDefinition.questions) {
        if (!question.id.trim()) return 'Each question must have an id';
        if (!question.title.trim()) return 'Each question must have a title';
        if (question.type === 'TWO_CHOICES') {
            if (!question.choice1.trim() || !question.choice2.trim()) {
                return 'TWO_CHOICES questions require both choices';
            }
        }
        if (question.type === 'IMAGE_GALLERY') {
            if (
                !Array.isArray(question.images) ||
                question.images.length === 0
            ) {
                return 'IMAGE_GALLERY questions must contain at least one image';
            }
            for (const image of question.images) {
                if (!image.key.trim()) {
                    return 'Each image in IMAGE_GALLERY must contain a key';
                }
                if (!(image.name ?? '').trim()) {
                    return 'Each image in IMAGE_GALLERY must contain a display name';
                }
                if (!image.image.trim()) {
                    return 'Each image in IMAGE_GALLERY must contain an image';
                }
            }
        }
    }
    return undefined;
}

export function StudyFormDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: StudyFormInput;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();

    const form = useForm<StudyFormInput>({ defaultValues });

    useEffect(() => {
        if (
            (mode === 'edit' || mode === 'version' || mode === 'show') &&
            record
        ) {
            form.reset({
                id: mode === 'version' ? undefined : record.id,
                title: record.title ?? '',
                enabled: record.enabled ?? true,
                missionTypeId: record.missionTypeId,
                whoCanFill: record.whoCanFill ?? 'VOLUNTEER',
                formDefinition: normalizeFormDefinition(record.formDefinition),
            });
            return;
        }
        form.reset(defaultValues);
    }, [mode, record, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async data => {
            const title = data.title?.trim() ?? '';
            const sanitizedDefinition =
                data.formDefinition &&
                sanitizeFormDefinition(data.formDefinition);

            const validationError = validateFormDefinition(sanitizedDefinition);
            if (validationError) {
                notify(validationError, { type: 'error' });
                return;
            }
            const payload = {
                title,
                enabled: data.enabled ?? true,
                missionTypeId: data.missionTypeId,
                whoCanFill: data.whoCanFill ?? 'VOLUNTEER',
                formDefinition: sanitizedDefinition,
            };
            try {
                if (mode === 'create' || mode === 'version') {
                    await create(
                        'studyForm',
                        { data: payload, meta: {} },
                        {
                            onSuccess: () => {
                                if (mode === 'version' && record?.id) {
                                    void update('studyForm', {
                                        id: record.id,
                                        data: {
                                            enabled: false,
                                            updatedAt: new Date().toISOString(),
                                        },
                                        previousData: record,
                                        meta: {},
                                    });
                                }
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'studyForm',
                        {
                            id: record.id,
                            data: payload,
                            previousData: record,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const saving = form.formState.isSubmitting;
    const canSave = mode !== 'show' && !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={record}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <StudyFormInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar sx={{ mt: 2, px: 0 }}>
                            <MuiButton
                                variant="contained"
                                type="submit"
                                disabled={!canSave}
                                startIcon={<ContentSaveIcon />}
                            >
                                Save
                            </MuiButton>
                            <MuiButton
                                variant="text"
                                onClick={onClose}
                                sx={{ ml: 1 }}
                                disabled={saving}
                            >
                                Cancel
                            </MuiButton>
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
